import { Dispatch, SetStateAction } from 'react';

import { Tooltip } from '@analog/ui/src';
import Badge from 'components/common/Badge';
import { Skeleton } from 'components/Skeleton';
import TableColumns from 'components/Table/TableColumns';
import { FontColors } from 'consts';
import moment from 'moment';
import { NavigateFunction } from 'react-router-dom';
import { routes } from 'routes/routesConst';
import { ColumnData } from 'types';
import {
  colors,
  colorType,
  DBtoStringValueMapping,
  getIcon,
  getLinkFromNetwork,
  getNetworkName,
  parseNetwork,
  timeAgo,
} from 'utils';
import { createDetailNavigation } from 'utils/navigation';

import { tooltipOfPhase } from './GMPMessageColumns';

interface TaskColumnProps {
  navigate: NavigateFunction;
  withType?: boolean;
  visibleColumns?: string[];
  setPhase?: Dispatch<SetStateAction<string>>;
  hidePhaseFilter?: boolean;
}

export const ReadTaskColumns = ({ navigate }: TaskColumnProps) => {
  const handleReadTaskClick = createDetailNavigation(navigate, routes.task);
  const columns = [
    {
      title: 'Task ID',
      key: 'taskId',
      render: (data: ColumnData) => (
        <>
          <Tooltip
            id={data[data.columnKey]?.toString() || ''}
            content={data[data.columnKey]?.toString() || ''}
            className="!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
          />
          <TableColumns
            dataTooltipId={`${data[data.columnKey]}`}
            value={data[data.columnKey] || '-'}
            classNames={{
              content: data[data.columnKey] ? 'cursor-pointer w-fit' : 'w-fit',
              skeletonText: 'h-4 w-10',
              skeletonLabel: 'w-10',
            }}
            textColor={FontColors.PURPLE}
            onClick={() => {
              data[data.columnKey]?.toString() && handleReadTaskClick(data?.taskId?.toString());
            }}
          />
        </>
      ),
    },
    {
      title: 'Block Range',
      key: 'definition',
      render: (data: any) => {
        const networkName = getNetworkName(data.network);
        return (
          <>
            <Tooltip
              id={data[data.columnKey]?.readGatewayEvents?.blocks[0].toString()}
              content={data[data.columnKey]?.readGatewayEvents?.blocks[0]}
              className="!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
            />
            <Tooltip
              id={data[data.columnKey]?.readGatewayEvents?.blocks[1].toString()}
              content={data[data.columnKey]?.readGatewayEvents?.blocks[1]}
              className="!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
            />
            <div className="flex flex-col gap-2 relative">
              <Skeleton.Loader>
                {data[data.columnKey] && (
                  <div className="flex flex-col gap-2">
                    <div className={'flex items-center gap-2 w-full'}>
                      <span className="w-[7px] h-[7px] bg-[#ffffffa3] flex rounded-full"></span>
                      <TableColumns
                        dataTooltipId={data[
                          data.columnKey
                        ]?.readGatewayEvents?.blocks[0].toString()}
                        value="Start Block"
                        classNames={{
                          content: 'cursor-pointer w-fit leading-[22px]',
                          skeletonText: 'h-4 w-10',
                          skeletonLabel: 'w-10',
                        }}
                        textColor={FontColors.PURPLE}
                        onClick={() => {
                          const redirectLink = getLinkFromNetwork(
                            `block/${data[data.columnKey].readGatewayEvents.blocks[0]}`,
                            networkName
                          );
                          redirectLink && window.open(redirectLink, '_blank');
                        }}
                      />
                    </div>
                    <div className={'flex items-center gap-2 w-full'}>
                      <span className="w-[7px] h-[7px] border border-[#ffffffa3] flex rounded-full before:content before:absolute before:border-r before:border-dashed before:border-[#ffffff3d] before:h-[18px] before:bottom-[17px] before:left-[2.5px] before:border-l before:top-[17px]"></span>
                      <TableColumns
                        dataTooltipId={data[
                          data.columnKey
                        ]?.readGatewayEvents?.blocks[1].toString()}
                        value="End Block"
                        onClick={() => {
                          const redirectLink = getLinkFromNetwork(
                            `block/${data[data.columnKey]?.readGatewayEvents?.blocks[1]}`,
                            networkName
                          );
                          redirectLink && window.open(redirectLink, '_blank');
                        }}
                        textColor={FontColors.PURPLE}
                        classNames={{
                          content: 'cursor-pointer w-fit leading-[22px]',
                          skeletonText: 'h-4 w-10',
                          skeletonLabel: 'w-10',
                        }}
                      />
                    </div>
                  </div>
                )}
              </Skeleton.Loader>
            </div>
          </>
        );
      },
    },
    {
      title: 'Network',
      key: 'network',
      render: (data: ColumnData) => {
        if (!data.network) {
          return (
            <Skeleton.Loader>
              <span>-</span>
            </Skeleton.Loader>
          );
        }
        const networkName = getNetworkName(data.network);
        return (
          <Skeleton.Loader>
            <div className="flex flex-col gap-2">
              <TableColumns
                key={data[data.columnKey]}
                beforeImagePath={networkName && getIcon(networkName)}
                value={parseNetwork(data.network)}
                textColor={FontColors.WHITE}
                classNames={{
                  container: '!w-fit',
                  base: '!w-fit',
                  content: 'capitalize whitespace-normal',
                  skeletonText: 'h-4 w-12',
                }}
                width="100%"
              />
            </div>
          </Skeleton.Loader>
        );
      },
    },
    {
      title: 'Status',
      key: 'phase',
      render: (data: ColumnData) => {
        const val = data[data.columnKey]?.toUpperCase();
        return (
          <>
            <Tooltip
              id={tooltipOfPhase[val as keyof typeof tooltipOfPhase] || ''}
              content={tooltipOfPhase[val as keyof typeof tooltipOfPhase] || ''}
              className="max-w-[220px] md:!max-w-fit !rounded-lg z-10"
              clickable
            />
            <Skeleton.Loader className="w-20 h-5">
              <Badge
                label={
                  DBtoStringValueMapping[val?.toLowerCase() as keyof typeof DBtoStringValueMapping]
                }
                badgeColor={colors[val?.toLowerCase() as keyof typeof colors] as colorType}
                data-tooltip-id={tooltipOfPhase[val as keyof typeof tooltipOfPhase] || ''}
              />
              {data?.hasReceived && <Badge label="RECEIVED" badgeColor="blue" />}
              {data?.hasExecuted && <Badge label="EXECUTED" badgeColor="brightGreen" />}
              {data?.result?.err && (
                <>
                  <Tooltip
                    id="failed"
                    content={tooltipOfPhase['FAILED'] || ''}
                    className="max-w-[220px] !rounded-lg z-10"
                    clickable
                  />
                  <Badge label="FAILED" badgeColor="red" data-tooltip-id="failed" />
                </>
              )}
            </Skeleton.Loader>
          </>
        );
      },
    },
    {
      title: 'Created at',
      key: 'created_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]?.toString() || ''}
          dataTooltipContent={
            data[data.columnKey]
              ? moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')
              : ''
          }
          toolTipClass="!w-fit"
        />
      ),
    },
    {
      title: 'Updated at',
      key: 'updated_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]?.toString() || ''}
          dataTooltipContent={
            data[data.columnKey]
              ? moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')
              : ''
          }
          toolTipClass="!w-fit"
        />
      ),
    },
  ];

  return columns;
};
